import { useEffect, useState } from 'react'
import Button from '../../components/atoms/Buttons'
import FormInput from '../../components/atoms/Input/Form-Input'
import { useNavigate } from 'react-router-dom'
import { useForm, FormProvider } from 'react-hook-form'
import { useUserContext } from '../../context/UserContext'
import { FiEdit } from 'react-icons/fi'
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa'
import { useMutation } from '@apollo/client'
import { UPDATE_USER } from '../../apollo/mutations/updateUser'
import { createToast } from '../../utils/Toast'
import { fetchAuthSession } from 'aws-amplify/auth'
import Spinner from '../../components/Spinner/Spinner'

const Toast = createToast()

function ProfileForm({ isEditing, setIsEditing }) {
  const navigate = useNavigate()
  const { userData } = useUserContext()
  const [updateUser, { loading }] = useMutation(UPDATE_USER)

  const [stripeLoading, setStripeLoading] = useState(false)

  const methods = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      companyName: '',
    },
  })
  const { handleSubmit, reset } = methods

  useEffect(() => {
    if (userData) {
      reset({
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        companyName: userData.manufacturerObject?.brand,
      })
    }
  }, [userData, reset, isEditing])

  const handleEditBusinessInfo = () => {
    navigate('/onboarding?editMode=true')
  }

  const handleShopifyConnect = () => {
    navigate('/shopify-connect?editMode=true', { state: { brandName: userData?.brand } })
  }

  const handleStripeConnect = async () => {
    setStripeLoading(true)
    try {
      setStripeLoading(true)
      const { tokens } = await fetchAuthSession()
      const idToken = tokens.idToken?.toString()

      const resp = await fetch(import.meta.env.VITE_STRIPE_VALIDATE_URL, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      })

      const { stripeConnectUrl } = await resp.json()
      console.log('stripeConnectUrl', stripeConnectUrl)
      window.location.href = stripeConnectUrl
    } catch (error) {
      console.error('Stripe connect failed:', error)
    } finally {
      setStripeLoading(false)
    }
  }

  const onSubmit = async data => {
    const { data: response } = await updateUser({
      variables: {
        input: {
          firstName: data.firstName,
          lastName: data.lastName,
        },
      },
      refetchQueries: ['ClientQuery'],
    })

    if (response?.updateUser) {
      Toast.fire({
        icon: 'success',
        title: 'Profile updated successfully',
      })
      setIsEditing(false)
    }
  }

  return (
    <FormProvider {...methods}>
      {isEditing ? (
        // ─────────── EDIT MODE ───────────
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={`space-y-4 w-full mx-auto p-4 ${!isEditing ? 'max-w-lg' : ' max-w-sm'
            }`}
        >
          <FormInput
            name="firstName"
            type="text"
            label="First Name"
            placeholder="Enter first name"
            validations={{
              required: { value: true, message: 'First Name is required' },
            }}
          />
          <FormInput
            name="lastName"
            label="Last Name"
            placeholder="Enter last name"
            validations={{
              required: { value: true, message: 'Last Name is required' },
            }}
          />

          <div className="py-6 flex gap-4">
            <Button
              type="button"
              primaryOutline
              disabled={loading}
              onClick={() => setIsEditing(false)}
              className={`create-saddle-btn !w-auto px-6 cursor-pointer`}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              className={`create-saddle-btn !w-auto px-10 cursor-pointer`}
            >
              Update
            </Button>
          </div>
        </form>
      ) : (
        // ─────────── VIEW MODE ───────────
        <div className="space-y-4 w-full max-w-lg mx-auto p-4">
          <FormInput name="firstName" label="First Name" disabled />
          <FormInput name="lastName" label="Last Name" disabled />
          <FormInput name="email" label="Email" disabled />
          <FormInput name="companyName" label="Company Name" disabled />

          {/* Status Block Wrapper */}
          <div className="rounded-lg p-2 space-y-3 mt-6">
            {[
              {
                labelConnected: 'Business Information',
                value: userData?.manufacturerObject?.isBusinessInfo,
                onClick: handleEditBusinessInfo,
              },
              {
                labelConnected: 'Shopify Account',
                value: userData?.manufacturerObject?.isShopifyConnected,
                onClick: handleShopifyConnect,
              },
              {
                labelConnected: 'Stripe Account',
                value: userData?.manufacturerObject?.isStripeConnected,
                onClick: handleStripeConnect,
              },
            ].map(
              (
                { labelConnected, value, onClick },
                index,
              ) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-white p-3 rounded border"
                >
                  <div className="flex items-center gap-2">
                    {value ? (
                      <FaCheckCircle className="text-green-500" size={18} />
                    ) : (
                      <FaTimesCircle className="text-red-500" size={18} />
                    )}
                    <span className="font-semibold text-md font-montserrat">
                      {labelConnected}
                    </span>
                  </div>
                  {labelConnected.includes('Stripe') ? (
                    value ? null : stripeLoading ? (
                      <div>
                        <Spinner size={2} center={false} className='min-h-0' />
                      </div>
                    ) : (
                      <FiEdit
                        size={18}
                        onClick={!stripeLoading ? onClick : undefined}
                        className={`cursor-pointer text-gray-600 ${stripeLoading
                          ? 'opacity-50 pointer-events-none'
                          : 'hover:text-black'
                          }`}
                        aria-label="Edit"
                        title="Edit"
                      />
                    )
                  ) : labelConnected.includes('Shopify') && value ? null : (
                    <FiEdit
                      size={18}
                      onClick={!stripeLoading ? onClick : undefined}
                      className={`cursor-pointer text-gray-600 ${stripeLoading
                        ? 'opacity-50 pointer-events-none'
                        : 'hover:text-black'
                        }`}
                      aria-label="Edit"
                      title="Edit"
                    />
                  )}
                </div>
              ),
            )}
          </div>

          {/* General Buttons */}
          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            <Button
              onClick={() => setIsEditing(true)}
              className="create-saddle-btn !w-[150px] cursor-pointer"
            >
              Edit Profile
            </Button>
          </div>
        </div>
      )}
    </FormProvider>
  )
}

export default ProfileForm
